import React, { useEffect, useMemo, useState, useContext, useRef } from "react";
import { Table, Badge, Button, Popover, Tag, Modal, Tooltip, Select, Input, Switch } from "antd";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { deleteStrategy, getStrategies, squareOffAllPositions, stopAndExitStrategy, getBasketPositions, delinkStrategySignal, linkStrategySignal } from "../../../../api/strategyApi";
import { getTicksData } from "../../../../redux/actions/optionChain";
import { getFundsAction } from "../../../../redux/actions/brokerAction";
import { calculatePositionRealisedPnl, calculatePositionUnrealisedPnl, fmtINR } from "../../../../util/helper";
import BrokerService from "../../../../services/broker.service";
import "../QuiktradeAlgo.css";
import StrategyDataTabs from "../list-strategies/StrategyDataTabs";
import "../list-strategies/index.css";
import "./index.css";
import { Handshake, BarChart3, Bolt, Filter, RefreshCw, FileText, X } from "lucide-react";
import BrokerLogsModal from "./BrokerLogsModal";
import SLTPModal from "../../positionPage/position-comp/SLTPModal";
import PnlMtmModal from "../../../common/PnlMtmModal";
import { getSignalTypeLabel, StatusBadge, SignalNamesDisplay, getBrokerLabelUtil, getDisplayNumber } from "../utils";
import { updateBasketAction } from "../../../../redux/actions/brokerAction";
import PlusMinQty from "components/utilities/PlusMinQty";
import SubscriptionCheckModal from "../../../common/SubscriptionCheckModal";
import AppContext from "../../../../context/Context";
import { useToast } from "components/common/custom-toast/Toast";
import LinkedSignalsModal from "../LinkedSignalsModal";

const { confirm } = Modal;


const BrokersPage = () => {
  const {
    config: { isDark },
  } = useContext(AppContext);
  const isDarkRef = useRef(isDark);

  useEffect(() => {
    isDarkRef.current = isDark;
  }, [isDark]);

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const brokerOptions = useSelector((s) => s?.brokers?.list || []);
  const loggedInBrokers = useMemo(() => {
    return brokerOptions.filter(broker => broker?.status === 'loggedIn' && broker?.quickTrade === true && (broker?.purpose === 'algo' || broker?.type === 'papertrading'));
  }, [brokerOptions]);
  const [brokerToStrategies, setBrokerToStrategies] = useState({});
  const [expandedBrokerKeys, setExpandedBrokerKeys] = useState([]);
  const [actionLoading, setActionLoading] = useState({});
  const [isSLTPModalOpen, setIsSLTPModalOpen] = useState(false);
  const [selectedStrategy, setSelectedStrategy] = useState(null);
  const [isPnlModalOpen, setIsPnlModalOpen] = useState(false);
  const [isRmsModalOpen, setIsRmsModalOpen] = useState(false);
  const [rmsModalBroker, setRmsModalBroker] = useState(null);
  const [rmsValuesByBroker, setRmsValuesByBroker] = useState({});
  const [rmsErrorMessages, setRmsErrorMessages] = useState({
    maxLoss: "",
    maxProfit: ""
  });
  const [logsModalVisible, setLogsModalVisible] = useState(false);
  const [selectedBrokerForLogs, setSelectedBrokerForLogs] = useState({ id: null, name: "" });
  const [pnlModalConfig, setPnlModalConfig] = useState({ title: "PNL / MTM", defaultLevel: "basket", brokerId: null, brokerName: null, baskets: [], positions: [] });
  const [formDataMap, setFormDataMap] = useState({});
  const [isLinkedSignalsModalOpen, setIsLinkedSignalsModalOpen] = useState(false);
  const [modalSignalLinks, setModalSignalLinks] = useState([]);
  const [modalStrategyName, setModalStrategyName] = useState("");
  const [filters, setFilters] = useState({
    account: null,
    strategyName: "",
    profitLoss: null,
    status: null,
    intraPos: null,
    linkedSignal: null,
    activeStrategies: false
  });
  const [showFilters, setShowFilters] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const isLoggedIn = useSelector((s) => s?.user?.isLoggedIn === true);
  const brokerLoggedIn = useSelector((s) => s?.user?.brokerLoggedIn === true);
  const user = useSelector((s) => s?.user);
  const socketJWT = useSelector((s) => s.socketJwt?.data);
  const tradeFeedSource = useSelector((s) => s.tradeFeedSource?.source);
  const funds = useSelector((s) => s.margin?.funds);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const accessiblePlans = useSelector((state) => state?.packages?.accessiblePlans);
  const plansLoading = accessiblePlans === undefined;
  const hasAlgoAccess = accessiblePlans?.length > 0 ? accessiblePlans?.some(planName =>
    planName === 'QT-Unlimited' || planName === 'QT-Algo' || planName === 'trial'
  ) : false;

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const toast = useToast()
  const brokersList = useSelector((state) => state?.brokers?.list || []);

  const validateMaxLoss = (value) => {
    if (value === null || value === undefined || value === '') {
      setRmsErrorMessages((prev) => ({ ...prev, maxLoss: "" }));
      return true;
    }

    const parsedVal = parseFloat(value);
    if (Number.isNaN(parsedVal)) {
      setRmsErrorMessages((prev) => ({ ...prev, maxLoss: "" }));
      return true;
    }

    if (parsedVal > 0) {
      setRmsErrorMessages((prev) => ({ ...prev, maxLoss: "Max Loss cannot be positive" }));
      return false;
    }

    setRmsErrorMessages((prev) => ({ ...prev, maxLoss: "" }));
    return true;
  };

  const validateMaxProfit = (value) => {
    if (value === null || value === undefined || value === '') {
      setRmsErrorMessages((prev) => ({ ...prev, maxProfit: "" }));
      return true;
    }

    const parsedVal = parseFloat(value);
    if (Number.isNaN(parsedVal)) {
      setRmsErrorMessages((prev) => ({ ...prev, maxProfit: "" }));
      return true;
    }

    if (parsedVal < 0) {
      setRmsErrorMessages((prev) => ({ ...prev, maxProfit: "Max Profit cannot be negative" }));
      return false;
    }

    setRmsErrorMessages((prev) => ({ ...prev, maxProfit: "" }));
    return true;
  };

  const defaultRiskForm = {
    sltpType: "PNL",
    stopLossAbsolute: null,
    takeProfitAbsolute: null,
    trailReferenceAbsolute: null,
    trailStepAbsolute: null,
    profitLockAt: null,
    profitLockSl: null,
    profitLockTrailRef: null,
    profitLockTrailStep: null,
    slValueType: 0,
    tpValueType: 0,
    trailValueType: 2,
    riskCoverType: 0,
  };

  const getStrategyBasketId = (strategy, brokerIdPref) => {
    const first = Array.isArray(strategy?.brokers) && strategy.brokers.length ? (strategy.brokers[0]?.id || strategy.brokers[0]) : undefined;
    const brokerKey = brokerIdPref || first;
    return strategy?.basketId?.[brokerKey];
  };

  const getRiskFormByBasketId = (basketId) => {
    if (!basketId) return undefined;
    return formDataMap?.[basketId];
  };

  const isBrokersLoading = useSelector((state) => state?.brokers?.isLoading);
  const isPlansLoading = useSelector((state) => state?.packages?.isLoading);

  useEffect(() => {
    if (isBrokersLoading || isPlansLoading) {
      return;
    }

    if (!isLoggedIn) {
      toast.error("Please login to access Algo signals");
      navigate("/authentication/login");
      return;
    }

    if (hasAlgoAccess === false) {
      setShowSubscriptionModal(true);
      return;
    }

    if (!brokerLoggedIn) {
      toast.error("Please connect your broker account to access Algo signals");
      navigate("/broker-login");
      return;
    }

    const purposed = (brokersList || []).filter(b => (b.purpose === 'algo' || b.type === 'papertrading'));
    if (purposed.length === 0) {
      toast.error("Please use broker account with purpose 'algo' to access Algo signals");
      navigate("/broker-login");
      return;
    }

  }, [isLoggedIn, brokerLoggedIn, hasAlgoAccess, isPlansLoading, isBrokersLoading]);

  const getBrokerLabel = (idOrObj) => getBrokerLabelUtil(idOrObj, loggedInBrokers);


  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await getStrategies();
      if (res?.code === 200 && Array.isArray(res?.data)) {
        const basketIdsAll = res.data
          .flatMap(r => {
            try {
              const brokers = r?.brokers || [];
              return brokers.map(b => r?.basketId?.[String(b?.id || b)]).filter(Boolean);
            } catch { return []; }
          });
        const uniqueBasketIds = Array.from(new Set(basketIdsAll.map(String)));

        const basketPnlEntries = await Promise.all(uniqueBasketIds.map(async (bid) => {
          try {
            const pres = await getBasketPositions(bid);
            if (pres?.code === 200 && Array.isArray(pres?.data)) {
              const rawPositions = pres.data || [];
              const tokens = rawPositions.map(p => p?.instrumentToken).filter(Boolean);
              let ticks = {};
              if (tokens.length > 0) {
                try {
                  ticks = await getTicksData(tokens);
                } catch (e) {
                  ticks = {};
                }
              }
              const positionsWithPnl = rawPositions.map((p) => {
                const lp = Number(ticks?.[p.instrumentToken]?.lp || 0);
                const realized = Number(calculatePositionRealisedPnl(p));
                const unrealized = Number(calculatePositionUnrealisedPnl(p, lp));
                return { ...p, ltp: lp, realizedPNL: realized, unrealizedPNL: unrealized, totalPNL: realized + unrealized };
              });
              const sum = positionsWithPnl.reduce((acc, p) => acc + Number(p?.totalPNL || 0), 0);
              const realizedSum = positionsWithPnl.reduce((acc, p) => acc + Number(p?.realizedPNL || 0), 0);
              const unrealizedSum = positionsWithPnl.reduce((acc, p) => acc + Number(p?.unrealizedPNL || 0), 0);
              const strategy = res.data.find(s => {
                if (!s?.basketId) return false;
                return Object.values(s.basketId).some(id => String(id) === String(bid));
              });
              const usePerLot = !!strategy?.rms?.perLot;

              const combPremData = positionsWithPnl.reduce((acc, p) => {
                const netQty = parseFloat(p.netQuantity || 0);
                const qty = Math.abs(netQty);
                const lotSize = parseFloat(p.instrument?.lotSize);
                const lots = lotSize > 0 ? qty / lotSize : qty;
                const ltp = parseFloat(p.ltp || 0);

                if (lots !== 0) {
                  if (usePerLot) {
                    if (netQty > 0) acc.buy += ltp;
                    else acc.sell += ltp;
                  } else {
                    if (netQty > 0) acc.buy += ltp * lots;
                    else acc.sell += ltp * lots;
                  }
                }
                return acc;
              }, { buy: 0, sell: 0 });
              const combPrem = combPremData.buy - combPremData.sell;
              return [String(bid), { totalPnl: sum, combPrem, realizedPnl: realizedSum, unrealizedPnl: unrealizedSum, perLot: usePerLot }];
            }
          } catch { }
          return [String(bid), { totalPnl: 0, combPrem: 0, realizedPnl: 0, unrealizedPnl: 0 }];
        }));
        const basketPnlMap = basketPnlEntries.reduce((acc, [k, v]) => { acc[k] = v; return acc; }, {});

        const loggedInBrokerIds = new Set(loggedInBrokers.map(b => String(b?.id || b)));

        const brokerMap = new Map();
        const strategiesMap = {};
        res.data.forEach((s) => {
          const brokersToCount = s?.brokers || [];
          brokersToCount.forEach((b) => {
            const key = String(b?.id || b);
            if (!loggedInBrokerIds.has(key)) return;

            const basketIdForThis = (() => { try { return s?.basketId?.[key]; } catch { return undefined; } })();
            const basketData = basketPnlMap[String(basketIdForThis)] || { totalPnl: 0, combPrem: 0, realizedPnl: 0, unrealizedPnl: 0 };
            const computedStrategyPnl = Number(basketData.totalPnl || 0);
            const computedUnrealizedPnl = Number(basketData.unrealizedPnl || 0);
            const computedRealizedPnl = Number(basketData.realizedPnl || 0);
            const computedCombPrem = Number(basketData.combPrem || 0);


            const prev = brokerMap.get(key) || {
              brokerName: key,
              totalPnl: 0,
              unrealizedPnl: 0,
              realizedPnl: 0,
              totalPositions: 0,
              totalOrders: 0,
              marginAvailable: undefined,
              totalMargin: undefined,
            };
            brokerMap.set(key, {
              brokerName: key,
              totalPnl: prev.totalPnl + computedStrategyPnl,
              realizedPnl: prev.realizedPnl + computedRealizedPnl,
              unrealizedPnl: prev.unrealizedPnl + computedUnrealizedPnl,
              totalPositions: prev.totalPositions + Number(s?.totalPositions || 0),
              totalOrders: prev.totalOrders + Number(s?.totalOrders || 0),
              marginAvailable: prev.marginAvailable,
              totalMargin: prev.totalMargin,
            });
            if (!strategiesMap[key]) strategiesMap[key] = [];
            strategiesMap[key].push({ ...s, totalPnl: computedStrategyPnl, combPrem: computedCombPrem, realizedPnl: computedRealizedPnl, unrealizedPnl: computedUnrealizedPnl });
          });
        });

        const brokerIds = Array.from(brokerMap.keys());
        await Promise.all(brokerIds.map(async (brokerId) => {
          try {
            const fundsData = await dispatch(getFundsAction(brokerId));
            if (fundsData && fundsData.marginAvailable !== undefined) {
              const brokerData = brokerMap.get(brokerId);
              if (brokerData) {
                brokerData.marginAvailable = fundsData.marginAvailable;
                brokerData.totalMargin = fundsData.usedMargin;
                brokerMap.set(brokerId, brokerData);
              }
            }
          } catch (error) {
            console.error(`Failed to fetch funds for broker ${brokerId}:`, error);
          }
        }));

        setRows(Array.from(brokerMap.values()));
        setBrokerToStrategies(strategiesMap);

        try {
          const brokerIds = Array.from(brokerMap.keys());
          const rmsResults = await Promise.all(brokerIds.map(async (bid) => {
            try {
              const gr = await BrokerService.getBrokerAlgoRms(bid);
              const data = (gr?.code === 200 && gr?.data) ? gr.data : (gr?.data || gr);
              return [String(bid), {
                maxLoss: data?.maxLoss ?? data?.max_loss ?? null,
                maxProfit: data?.maxProfit ?? data?.max_profit ?? null
              }];
            } catch (e) {
              return [String(bid), { maxLoss: null, maxProfit: null }];
            }
          }));
          const rmsMap = rmsResults.reduce((acc, [k, v]) => { acc[k] = v; return acc; }, {});
          setRmsValuesByBroker(rmsMap);
        } catch (e) {
          console.error("Failed to load broker RMS settings", e);
        }

        try {
          const results = await Promise.all(uniqueBasketIds.map(async (bid) => {
            try {
              const bres = await BrokerService.getBasket(bid);
              const data = bres?.data || bres;
              const value = {
                sltpType: data?.rms?.basis === 2 ? "Underlying" : (data?.rms?.basis === 1 ? "Comb Prem" : "PNL"),
                stopLossAbsolute: data?.rms?.stopLossInput?.value ?? null,
                takeProfitAbsolute: data?.rms?.takeProfitInput?.value ?? null,
                trailReferenceAbsolute: data?.rms?.trailSL?.reference ?? null,
                trailStepAbsolute: data?.rms?.trailSL?.step ?? null,
                profitLockAt: data?.rms?.profitLockAt ?? null,
                profitLockSl: data?.rms?.profitLockSl ?? null,
                profitLockTrailRef: data?.rms?.profitLockTrail?.reference ?? null,
                profitLockTrailStep: data?.rms?.profitLockTrail?.step ?? null,
                slValueType: data?.rms?.stopLossInput?.valueType ?? 0,
                tpValueType: data?.rms?.takeProfitInput?.valueType ?? 0,
                trailValueType: data?.rms?.trailSL?.valueType ?? 2,
              };
              return [String(bid), value];
            } catch {
              return [String(bid), { sltpType: "PNL", stopLossAbsolute: null, takeProfitAbsolute: null, trailReferenceAbsolute: null, trailStepAbsolute: null, profitLockAt: null, profitLockSl: null, profitLockTrailRef: null, profitLockTrailStep: null, slValueType: 0, tpValueType: 0, trailValueType: 2, riskCoverType: 0 }];
            }
          }));
          const map = results.reduce((acc, [k, v]) => { acc[k] = v; return acc; }, {});
          setFormDataMap(map);
        } catch { }
      } else if (res?.sessionExpired) {
        toast.error("Session expired. Please login again.");
        navigate("/authentication/login");
      } else {
        toast.error(res?.error);
      }
    } catch (e) {
      toast.error("Failed to load brokers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (loggedInBrokers.length > 0) {
      fetchData();
    }
  }, [loggedInBrokers]);

  useEffect(() => {
    if (!socketJWT || !tradeFeedSource || loggedInBrokers.length === 0) return;

    const subscriptions = [];

    loggedInBrokers.forEach(broker => {
      const brokerChannel = `${socketJWT.wsId}-${broker.id}`;
      const subscription = tradeFeedSource
        .subscribe(brokerChannel, (message) => {
          try {
            const payload = message?.data;
            if (!payload) return;

            if (payload.channel === 'basket_mtm') {
              const mtm = payload?.mtm;
              if (mtm && typeof mtm.id !== 'undefined') {
                updateStrategyPnLElements(mtm);
              }
            }

            if (payload.channel === 'broker_algo_mtm') {
              const mtm = payload?.mtm;
              if (mtm && typeof mtm.id !== 'undefined') {
                updateBrokerPnLElement(mtm);
              }
            }

            if (payload.channel === 'algo_baskets') {
              const basket = payload?.basket;
              if (basket?.id) {
                setFormDataMap(prev => {
                  const currentFormData = prev?.[String(basket.id)] || defaultRiskForm;
                  const updatedFormData = {
                    ...currentFormData,
                    ...({ stopLossAbsolute: (basket.rms?.stopLoss ? basket.rms?.stopLoss : basket.rms?.stopLossInput?.value) ?? null }),
                    ...({ takeProfitAbsolute: (basket.rms?.takeProfit ? basket.rms?.takeProfit : basket.rms?.takeProfitInput?.value) ?? null }),
                    ...({ trailReferenceAbsolute: basket.rms?.trailSL?.reference ?? null }),
                    ...({ trailStepAbsolute: basket.rms?.trailSL?.step ?? null }),
                    ...({ profitLockAt: basket.rms?.profitLockAt ?? null }),
                    ...({ profitLockSl: basket.rms?.profitLockSl ?? null }),
                    ...({ profitLockTrailRef: basket.rms?.profitLockTrail?.reference ?? null }),
                    ...({ profitLockTrailStep: basket.rms?.profitLockTrail?.step ?? null }),
                    ...({ slValueType: basket.rms?.stopLossInput?.valueType ?? 0 }),
                    ...({ tpValueType: basket.rms?.takeProfitInput?.valueType ?? 0 }),
                    ...({ trailValueType: basket.rms?.trailSL?.valueType ?? 2 }),
                    basis: basket.rms?.basis ?? 0
                  };
                  return {
                    ...prev,
                    [String(basket.id)]: updatedFormData
                  };
                });
              }
            }

            if (payload.channel === 'algo_logs') {
              const log = payload?.log;
              if (log) {
                const message = log?.msg?.trim();

                if (log?.color === 'red') {
                  toast.error(message);
                } else {
                  toast.success(message);
                }
              }
            }
          } catch (e) {
            console.error(`Error processing message for broker ${broker.id}:`, e);
          }
        })
        .on('subscribe', () => {
          console.log(`Subscribed to channel for broker ${broker.id}`);
        })
        .on('error', (error) => {
          console.error(`Error in subscription for broker ${broker.id}:`, error);
        });

      subscriptions.push(subscription);
    });

    return () => {
      subscriptions.forEach(subscription => {
        try {
          subscription?.unsubscribe();
        } catch (e) {
          console.error('Error unsubscribing from PnL updates:', e);
        }
      });
    };
  }, [socketJWT, tradeFeedSource, loggedInBrokers]);

  const updateBrokerPnLElement = (mtm) => {
    if (!mtm?.id) return;
    const brokerPnlEl = document.getElementById(`broker-pnl-${mtm.id}`);
    if (brokerPnlEl) {
      const pnl = Number(mtm?.total_pnl ?? 0);
      brokerPnlEl.textContent = fmtINR(pnl.toFixed(2));
      brokerPnlEl.style.color = pnl === 0 ? '' : (pnl > 0 ? (isDarkRef.current ? "#00d27a" : "#16a34a") : (isDarkRef.current ? "#F87171" : "#E74C3C"));
    }

    const brokerUPnlEl = document.getElementById(`broker-upnl-${mtm.id}`);
    if (brokerUPnlEl) {
      const upnl = Number(mtm?.unrealised_pnl ?? 0);
      brokerUPnlEl.textContent = fmtINR(upnl.toFixed(2));
      brokerUPnlEl.style.color = upnl === 0 ? '' : (upnl > 0 ? (isDarkRef.current ? "#00d27a" : "#16a34a") : (isDarkRef.current ? "#F87171" : "#E74C3C"));
    }

    const brokerRPnlEl = document.getElementById(`broker-rpnl-${mtm.id}`);
    if (brokerRPnlEl) {
      const rpnl = Number(mtm?.realised_pnl ?? 0);
      brokerRPnlEl.textContent = fmtINR(rpnl.toFixed(2));
      brokerRPnlEl.style.color = rpnl === 0 ? '' : (rpnl > 0 ? (isDarkRef.current ? "#00d27a" : "#16a34a") : (isDarkRef.current ? "#F87171" : "#E74C3C"));
    }
  };

  // Helper function to update strategy PnL elements
  const updateStrategyPnLElements = (mtm) => {
    if (!mtm?.id) return;

    // Update total PnL
    const pnlEl = document.getElementById(`pnl-${mtm.id}`);
    if (pnlEl) {
      const pnl = Number(mtm?.total_pnl ?? 0);
      pnlEl.textContent = fmtINR(pnl.toFixed(2));
      pnlEl.style.color = pnl === 0 ? '' : (pnl > 0 ? (isDarkRef.current ? "#00d27a" : "#16a34a") : (isDarkRef.current ? "#F87171" : "#E74C3C"));
    }

    const upnlEl = document.getElementById(`upnl-${mtm.id}`);
    if (upnlEl) {
      const upnl = Number(mtm?.unrealised_pnl ?? 0);
      upnlEl.textContent = fmtINR(upnl.toFixed(2));
      upnlEl.style.color = upnl === 0 ? '' : (upnl > 0 ? (isDarkRef.current ? "#00d27a" : "#16a34a") : (isDarkRef.current ? "#F87171" : "#E74C3C"));
    }

    const rpnlEl = document.getElementById(`rpnl-${mtm.id}`);
    if (rpnlEl) {
      const rpnl = Number(mtm?.realised_pnl ?? 0);
      rpnlEl.textContent = fmtINR(rpnl.toFixed(2));
      rpnlEl.style.color = rpnl === 0 ? '' : (rpnl > 0 ? (isDarkRef.current ? "#00d27a" : "#16a34a") : (isDarkRef.current ? "#F87171" : "#E74C3C"));
    }
  };


  const stopStrategy = async (strategyId) => {
    try {
      const res = await stopAndExitStrategy(strategyId)
      if (res.code === 200) {
        toast.success('Strategy stopped successfully');
        await fetchData();
      } else {
        throw new Error('Failed to stop strategy');
      }
    } catch (error) {
      console.error('Error stopping strategy:', error);
      toast.error('Failed to stop strategy');
    }
  };

  const squareOffStrategy = async (strategyId) => {
    try {
      const res = await squareOffAllPositions(strategyId)
      if (res?.code === 200) {
        toast.success('Strategy squared off successfully');
        await fetchData();
      } else {
        throw new Error('Failed to square off strategy');
      }
    } catch (error) {
      console.error('Error squaring off strategy:', error);
      toast.error('Failed to square off strategy');
    }
  };

  const handleStopAndExit = (record) => {
    confirm({
      title: 'Square off & Pause',
      icon: null,
      content: `Are you sure you want to square off & pause the strategy "${record.name}"? This action cannot be undone.`,
      okText: 'Yes, Square off & Pause',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        setActionLoading(prev => ({ ...prev, [`stop_${record.id}`]: true }));
        await stopStrategy(record.id);
        setActionLoading(prev => ({ ...prev, [`stop_${record.id}`]: false }));
      },
    });
  };

  const handleSquareOffAll = (record) => {
    confirm({
      title: 'Square Off All Positions',
      icon: null,
      content: `Are you sure you want to square off all positions for the strategy "${record.name}"?`,
      okText: 'Yes, Square Off All',
      okType: 'primary',
      cancelText: 'Cancel',
      onOk: async () => {
        setActionLoading(prev => ({ ...prev, [`squareoff_${record.id}`]: true }));
        await squareOffStrategy(record.id);
        setActionLoading(prev => ({ ...prev, [`squareoff_${record.id}`]: false }));
      },
    });
  };

  const handleDeleteStrategy = async (record) => {
    try {
      const res = await deleteStrategy(record.id);
      if (res?.code === 200) {
        toast.success('Strategy deleted');
        await fetchData();
      } else if (res?.sessionExpired) {
        toast.error('Session expired. Please login again.');
      } else {
        toast.error(res?.error || 'Failed to delete');
      }
    } catch (e) {
      console.error("Error:" + e)
    }
  };

  const handleDeleteConfirm = (record) => {
    confirm({
      title: 'Delete Strategy',
      icon: null,
      content: `Are you sure you want to delete the strategy "${record.name}"? This action cannot be undone.`,
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        setActionLoading(prev => ({ ...prev, [`delete_${record.id}`]: true }));
        await handleDeleteStrategy(record);
        setActionLoading(prev => ({ ...prev, [`delete_${record.id}`]: false }));
      },
    });
  };

  const handleToggleSignalLink = async (record) => {
    const isDelinked = record.isSignalDelinked;
    const action = isDelinked ? 'relink' : 'delink';
    const strategyId = record.id;

    confirm({
      title: isDelinked ? 'Relink Signals' : 'Delink Signals',
      content: `Are you sure you want to ${action} signals for strategy "${record.name}"?`,
      okText: isDelinked ? 'Relink' : 'Delink',
      okType: isDelinked ? 'primary' : 'danger',
      icon: null,
      onOk: async () => {
        try {
          setActionLoading(prev => ({ ...prev, [`signal_${strategyId}`]: true }));
          const res = isDelinked ? await linkStrategySignal(strategyId) : await delinkStrategySignal(strategyId);
          if (res?.code === 200) {
            toast.success(`Signals ${isDelinked ? 'relinked' : 'delinked'} successfully`);
            await fetchData();
          } else {
            toast.error(res?.error || `Failed to ${action} signals`);
          }
        } catch (error) {
          console.error(`Error ${action} signals:`, error);
          toast.error(`Failed to ${action} signals`);
        } finally {
          setActionLoading(prev => ({ ...prev, [`signal_${strategyId}`]: false }));
        }
      }
    });
  };

  const handleOpenSLTPModal = (record, brokerId) => {
    setSelectedStrategy({ ...record, currentBrokerId: brokerId });
    setIsSLTPModalOpen(true);
  };

  const openRmsModalForBroker = (broker) => {
    setRmsModalBroker({ brokerId: broker?.brokerName, brokerName: getBrokerLabel(broker?.brokerName) });
    setIsRmsModalOpen(true);
    setRmsErrorMessages({ maxLoss: "", maxProfit: "" });
  };

  const columns = useMemo(
    () => [
      {
        title: "Broker Name", dataIndex: "brokerName", key: "brokerName", align: "left", render: (v) => (
          <span>{getBrokerLabel(v)}</span>
        )
      },
      {
        title: "PNL", dataIndex: "totalPnl", key: "pnl", align: "right", render: (v, record) => {
          const numValue = Number(v || 0);
          const color = numValue === 0 ? '' : (numValue > 0 ? (isDark ? "#00d27a" : "#16a34a") : (isDark ? "#F87171" : "#E74C3C"));
          const displayValue = numValue > 0 ? `+${fmtINR(numValue.toFixed(2))}` : fmtINR(numValue.toFixed(2));
          return (
            <div className="pnl-container">
              <span id={`broker-pnl-${record?.brokerName}`} style={{ color, whiteSpace: "nowrap" }}>{displayValue}</span>
              <Tooltip title="PNL Graph">
                <Button
                  type="text"
                  size="small"
                  icon={<BarChart3 className="pnl-graph" />}
                  onClick={() => {
                    const strategies = brokerToStrategies[record.brokerName] || [];
                    const baskets = strategies
                      .map(s => ({
                        id: s.basketId[record.brokerName],
                        name: s.name
                      }))
                      .filter(b => b.id);

                    setPnlModalConfig({
                      title: `PNL / MTM - ${getBrokerLabel(record.brokerName)}`,
                      defaultLevel: 'algo-broker',
                      brokerId: record.brokerName,
                      brokerName: getBrokerLabel(record.brokerName),
                      baskets: baskets,
                      positions: [],
                    });
                    setIsPnlModalOpen(true);
                  }}
                />
              </Tooltip>
            </div>
          )
        }
      },
      {
        title: "Unrealized", dataIndex: "unrealizedPnl", key: "upnl", align: "right", render: (v, record) => {
          const numValue = Number(v || 0);
          const color = numValue === 0 ? '' : (numValue > 0 ? (isDark ? "#00d27a" : "#16a34a") : (isDark ? "#F87171" : "#E74C3C"));
          const displayValue = numValue > 0 ? `+${fmtINR(numValue.toFixed(2))}` : fmtINR(numValue.toFixed(2));
          return <span id={`broker-upnl-${record?.brokerName}`} style={{ color, whiteSpace: "nowrap" }}>{displayValue}</span>;
        }
      },
      {
        title: "Realized", dataIndex: "realizedPnl", key: "rpnl", align: "right", render: (v, record) => {
          const numValue = Number(v || 0);
          const color = numValue === 0 ? '' : (numValue > 0 ? (isDark ? "#00d27a" : "#16a34a") : (isDark ? "#F87171" : "#E74C3C"));
          const displayValue = numValue > 0 ? `+${fmtINR(numValue.toFixed(2))}` : fmtINR(numValue.toFixed(2));
          return <span id={`broker-rpnl-${record?.brokerName}`} style={{ color, whiteSpace: "nowrap" }}>{displayValue}</span>;
        }
      },
      {
        title: "Total Funds", dataIndex: "marginAvailable", key: "marginAvailable", align: "right", render: (v) => (
          <span>{fmtINR(Number(v || 0).toFixed(2))}</span>
        )
      },
      {
        title: "Used Funds", dataIndex: "totalMargin", key: "totalMargin", align: "right", render: (v) => (
          <span>{fmtINR(Number(v || 0).toFixed(2))}</span>
        )
      },
      {
        title: "RMS Settings",
        key: "rmsSettings",
        align: "center",
        render: (_, record) => {
          return (
            <div style={{ display: "flex", gap: "8px", justifyContent: "center", alignItems: "center" }}>
              <Tooltip title="Edit RMS">
                <Bolt
                  style={{ cursor: "pointer" }}
                  strokeWidth={1.5}
                  size={18}
                  onClick={() => openRmsModalForBroker(record)}
                />
              </Tooltip>
            </div>
          );
        }
      },
      {
        title: "Logs",
        key: "logs",
        align: "center",
        render: (_, record) => {
          return (
            <Button
              size="small"
              icon={<FileText strokeWidth={1.5} size={16} />}
              onClick={() => {
                setSelectedBrokerForLogs({ id: record.brokerName, name: getBrokerLabel(record.brokerName) });
                setLogsModalVisible(true);
              }}
            />
          );
        }
      },
      {
        title: "Strat Count",
        dataIndex: "brokerName",
        key: "strategyCount",
        align: "right",
        render: (brokerName, record) => {
          const count = record._filteredStrategyCount !== undefined
            ? record._filteredStrategyCount
            : (brokerToStrategies[brokerName]?.length || 0);
          return <span>{count}</span>;
        }
      },
      { title: "Total Positions", dataIndex: "totalPositions", key: "totalPositions", align: "right" },
      { title: "Total Orders", dataIndex: "totalOrders", key: "totalOrders", align: "right" },
    ],
    [loggedInBrokers, brokerToStrategies, isDark]
  );

  const getStrategyColumns = (currentBrokerId) => [
    { title: "Strategy Name", align: "left", dataIndex: "name", key: "name" },
    {
      title: "Symbol",
      align: "left",
      dataIndex: ["underlyingInstrument", "underlying"],
      key: "symbol",
      render: (v) => <span className="symbol-badge">{v}</span>
    },
    {
      title: "PNL",
      dataIndex: "totalPnl",
      align: "right",
      key: "pnl",
      render: (v, record) => {
        const brokerId = currentBrokerId || record?.brokers?.[0];
        return (
          <div className="pnl-container">
            <p id={`pnl-${getStrategyBasketId(record, brokerId)}`} style={{
              color: Number(v) === 0 ? '' : (Number(v) > 0 ? (isDark ? "#00d27a" : "#16a34a") : (isDark ? "#F87171" : "#E74C3C")),
              width: "14ch",
              fontSize: "12px"
            }}>
              {Number(v) > 0 ? `+${fmtINR(Number(v || 0).toFixed(2))}` : fmtINR(Number(v || 0).toFixed(2))}
            </p>
            <Tooltip title="PNL Graph">
              <Button
                type="text"
                size="small"
                icon={<BarChart3 className="pnl-graph" />}
                onClick={() => {
                  const basketId = getStrategyBasketId(record, brokerId);
                  if (!basketId) return;
                  setPnlModalConfig({
                    title: `PNL / MTM - ${record.name}`,
                    defaultLevel: 'basket',
                    brokerId: null,
                    brokerName: null,
                    baskets: [{ id: basketId, name: record.name }],
                    positions: [],
                  });
                  setIsPnlModalOpen(true);
                }}
              />
            </Tooltip>
          </div>
        );
      }
    },
    {
      title: "Unrealized", dataIndex: "unrealizedPnl", key: "upnl", align: "right", render: (v, record) => {
        const brokerId = currentBrokerId || record?.brokers?.[0];
        return (
          <p id={`upnl-${getStrategyBasketId(record, brokerId)}`} style={{ color: Number(v) === 0 ? '' : (Number(v) > 0 ? (isDark ? "#00d27a" : "#16a34a") : (isDark ? "#F87171" : "#E74C3C")), width: "14ch", fontSize: "12px" }}>
            {Number(v) > 0 ? `+${fmtINR(Number(v || 0).toFixed(2))}` : fmtINR(Number(v || 0).toFixed(2))}
          </p>
        );
      }
    },
    {
      title: "Realized", dataIndex: "realizedPnl", key: "rpnl", align: "right", render: (v, record) => {
        const brokerId = currentBrokerId || record?.brokers?.[0];
        return (
          <p id={`rpnl-${getStrategyBasketId(record, brokerId)}`} style={{ color: Number(v) === 0 ? '' : (Number(v) > 0 ? (isDark ? "#00d27a" : "#16a34a") : (isDark ? "#F87171" : "#E74C3C")), width: "14ch", fontSize: "12px" }}>
            {Number(v) > 0 ? `+${fmtINR(Number(v || 0).toFixed(2))}` : fmtINR(Number(v || 0).toFixed(2))}
          </p>
        );
      }
    },
    {
      title: "Stoploss", dataIndex: "stopLossAbsolute", key: "sl", align: "right", render: (_, record) => {
        const brokerId = currentBrokerId || record?.brokers?.[0];
        const bid = getStrategyBasketId(record, brokerId);
        const f = getRiskFormByBasketId(bid);
        return getDisplayNumber(f?.stopLossAbsolute);
      }
    },
    {
      title: "Target", dataIndex: "takeProfitAbsolute", key: "tp", align: "right", render: (_, record) => {
        const brokerId = currentBrokerId || record?.brokers?.[0];
        const bid = getStrategyBasketId(record, brokerId);
        const f = getRiskFormByBasketId(bid);
        return getDisplayNumber(f?.takeProfitAbsolute);
      }
    },
    {
      title: "SL Trailing", key: "sltrail", align: "right", render: (_, record) => {
        const brokerId = currentBrokerId || record?.brokers?.[0];
        const bid = getStrategyBasketId(record, brokerId);
        const f = getRiskFormByBasketId(bid);
        if (!f) return '-';
        const ref = f?.trailReferenceAbsolute;
        const step = f?.trailStepAbsolute;
        if (ref === 0) return 0;
        if (step === 0) return 0;
        return (ref && step) ? `${ref} / ${step}` : '-';
      }
    },
    {
      title: "Profit lock/trail", key: "plock", align: "right", render: (_, record) => {
        const brokerId = currentBrokerId || record?.brokers?.[0];
        const bid = getStrategyBasketId(record, brokerId);
        const f = getRiskFormByBasketId(bid);
        if (!f) return '-';
        const left = f.profitLockAt && f.profitLockSl ? `${f.profitLockAt} - ${f.profitLockSl}` : '-';
        const right = f.profitLockTrailRef && f.profitLockTrailStep ? `${f.profitLockTrailRef} - ${f.profitLockTrailStep}` : '-';
        return <span style={{ whiteSpace: "nowrap" }}>{left}  /  {right}</span>;
      }
    },
    {
      title: "Linked Signals",
      align: "center",
      dataIndex: "signalLinks",
      key: "signal",
      render: (signalLinks, record) => (
        <SignalNamesDisplay
          signalLinks={signalLinks}
          onIconClick={(links) => {
            setModalSignalLinks(links);
            setModalStrategyName(record?.name || "");
            setIsLinkedSignalsModalOpen(true);
          }}
        />
      )
    },
    {
      title: "Status",
      align: "left",
      dataIndex: "status",
      key: "status",
      render: (s) => <StatusBadge status={s} />
    },
    { title: "Intra / Pos", align: "left", dataIndex: "positionalOrIntraday", key: "ip", render: (value) => (value ? "Positional" : "Intraday") },
    {
      title: "Risk Settings", align: "center", key: "risk", render: (_, record) => (
        <Bolt style={{ cursor: 'pointer' }} strokeWidth={1.5} size={18} onClick={() => handleOpenSLTPModal(record, currentBrokerId)} />
      )
    },
    {
      title: "Actions",
      align: "center",
      key: "actions",
      render: (_, record) => (
        <div className="actions-container">
          <Button
            size="small"
            loading={actionLoading[`stop_${record.id}`]}
            onClick={() => handleStopAndExit(record)}
            className="square-off-pause-btn"
          >
            Square off & Pause
          </Button>
          <Button
            size="small"
            loading={actionLoading[`squareoff_${record.id}`]}
            onClick={() => handleSquareOffAll(record)}
            className="square-off-btn"
          >
            Square off All
          </Button>
          <Button
            size="small"
            loading={actionLoading[`signal_${record.id}`]}
            onClick={() => handleToggleSignalLink(record)}
            className="signal-link-btn"
          >
            {record.isSignalDelinked ? 'Relink Signal' : 'Delink Signal'}
          </Button>
          <Button onClick={() => navigate(`/algo/edit-strategy/${record.id}`)} size="small" type="link" className="edit-strat-btn">
            Edit
          </Button>
          <Button
            size="small"
            loading={actionLoading[`delete_${record.id}`]}
            onClick={() => handleDeleteConfirm(record)}
            className="delete-strat-btn"
          >
            Delete
          </Button>
        </div>
      )
    },
  ];

  const getRmsForBroker = (brokerId) => {
    if (!brokerId) return { maxLoss: null, maxProfit: null };
    return rmsValuesByBroker[brokerId] || { maxLoss: null, maxProfit: null };
  };

  const handleRmsChange = (field, value, brokerId) => {
    setRmsValuesByBroker(prev => ({
      ...prev,
      [String(brokerId)]: {
        ...(prev?.[String(brokerId)] || {}),
        [field]: value,
      }
    }));

    if (field === "maxLoss") {
      validateMaxLoss(value);
    } else if (field === "maxProfit") {
      validateMaxProfit(value);
    }
  };


  const handleSaveRmsForBroker = async () => {
    try {
      const brokerId = rmsModalBroker?.brokerId;
      const currentRms = getRmsForBroker(brokerId);

      const isMaxLossValid = validateMaxLoss(currentRms.maxLoss);
      const isMaxProfitValid = validateMaxProfit(currentRms.maxProfit);

      if (!isMaxLossValid || !isMaxProfitValid) {
        return;
      }

      const payload = {
        maxLoss: currentRms.maxLoss ?? null,
        maxProfit: currentRms.maxProfit ?? null,
      };

      const res = await BrokerService.updateBrokerAlgoRms(brokerId, payload);
      if (res?.code === 200) {
        toast.success("RMS settings saved for broker");
        setFormDataMap(prev => ({
          ...(prev || {}),
          [`broker_${String(brokerId)}`]: {
            ...(prev?.[`broker_${String(brokerId)}`] || {}),
            maxLoss: payload.maxLoss,
            maxProfit: payload.maxProfit,
          }
        }));
        setIsRmsModalOpen(false);
        setRmsModalBroker(null);
      } else if (res?.sessionExpired) {
        toast.error("Session expired. Please login again.");
        navigate("/authentication/login");
      } else {
        throw new Error(res?.error || "Failed to save RMS");
      }
    } catch (e) {
      console.error("Error saving broker RMS:", e);
      toast.error("Failed to save RMS settings");
    }
  };

  // Filter functions
  const getUniqueSignals = () => {
    const signals = new Set();
    Object.values(brokerToStrategies).forEach(strategies => {
      strategies.forEach(strategy => {
        if (Array.isArray(strategy.signalLinks)) {
          strategy.signalLinks.forEach(signal => {
            if (signal?.name) {
              signals.add(signal.name);
            }
          });
        }
      });
    });
    return Array.from(signals).sort();
  };

  const applyFilters = (brokerRow, strategies) => {
    let filteredStrategies = strategies;

    if (filters.strategyName) {
      const searchTerm = filters.strategyName.toLowerCase();
      filteredStrategies = filteredStrategies.filter(s =>
        s.name?.toLowerCase().includes(searchTerm)
      );
    }

    if (filters.profitLoss === 'profit') {
      filteredStrategies = filteredStrategies.filter(s => Number(s.totalPnl || 0) > 0);
    } else if (filters.profitLoss === 'loss') {
      filteredStrategies = filteredStrategies.filter(s => Number(s.totalPnl || 0) < 0);
    }

    if (filters.status !== null) {
      filteredStrategies = filteredStrategies.filter(s => s.status === filters.status);
    }

    if (filters.intraPos === 'intraday') {
      filteredStrategies = filteredStrategies.filter(s => s.positionalOrIntraday === false);
    } else if (filters.intraPos === 'positional') {
      filteredStrategies = filteredStrategies.filter(s => s.positionalOrIntraday === true);
    }

    if (filters.linkedSignal) {
      filteredStrategies = filteredStrategies.filter(s => {
        if (!Array.isArray(s.signalLinks)) return false;
        return s.signalLinks.some(signal => signal?.name === filters.linkedSignal);
      });
    }

    if (filters.activeStrategies) {
      filteredStrategies = filteredStrategies.filter(s => Number(s.totalPnl || 0) !== 0);
    }

    return filteredStrategies;
  };

  // Apply filters to rows and calculate filtered totals
  const filteredData = useMemo(() => {
    let filteredRows = [...rows];

    // Filter by account
    if (filters.account) {
      filteredRows = filteredRows.filter(row => row.brokerName === filters.account);
    }

    // Apply strategy-level filters and recalculate broker totals
    filteredRows = filteredRows.map(brokerRow => {
      const strategies = brokerToStrategies[brokerRow.brokerName] || [];
      const filteredStrategies = applyFilters(brokerRow, strategies);

      // Recalculate totals based on filtered strategies
      const totalPnl = filteredStrategies.reduce((sum, s) => sum + Number(s.totalPnl || 0), 0);
      const realizedPnl = filteredStrategies.reduce((sum, s) => sum + Number(s.realizedPnl || 0), 0);
      const unrealizedPnl = filteredStrategies.reduce((sum, s) => sum + Number(s.unrealizedPnl || 0), 0);
      const totalPositions = filteredStrategies.reduce((sum, s) => sum + Number(s.totalPositions || 0), 0);
      const totalOrders = filteredStrategies.reduce((sum, s) => sum + Number(s.totalOrders || 0), 0);

      return {
        ...brokerRow,
        totalPnl,
        realizedPnl,
        unrealizedPnl,
        totalPositions,
        totalOrders,
        _filteredStrategyCount: filteredStrategies.length
      };
    });

    if (filters.strategyName || filters.profitLoss || filters.status !== null ||
      filters.intraPos || filters.linkedSignal) {
      filteredRows = filteredRows.filter(row => row._filteredStrategyCount > 0);
    }

    return filteredRows;
  }, [rows, brokerToStrategies, filters]);

  const clearFilters = () => {
    setFilters({
      account: null,
      strategyName: "",
      profitLoss: null,
      status: null,
      intraPos: null,
      linkedSignal: null,
      activeStrategies: false
    });
  };

  const hasActiveFilters = filters.account || filters.strategyName ||
    filters.profitLoss || filters.status !== null || filters.intraPos ||
    filters.linkedSignal || filters.activeStrategies;

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await fetchData();
      toast.success('Data refreshed successfully');
    } catch (error) {
      toast.error('Failed to refresh data');
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="quiktrade-algo">
      <div className="algo-content">
        <div className="create-strategy-container">
          <div className="config-section">
            <div style={{ marginBottom: "1.5rem" }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', flexWrap: 'wrap' }} className="qt-algo-section-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Handshake size={20} />
                  <h3>Accounts Dashboard</h3>
                </div>
                <div style={{
                  display: 'flex',
                  gap: '12px',
                  flexWrap: 'wrap',
                }}>

                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: 500 }}>
                      Account
                    </label>
                    <Select
                      style={{ width: '130px' }}
                      placeholder="All Accounts"
                      value={filters.account}
                      onChange={(val) => setFilters(prev => ({ ...prev, account: val }))}
                      allowClear
                    >
                      {loggedInBrokers.map(broker => (
                        <Select.Option key={broker.id} value={String(broker.id)}>
                          {getBrokerLabel(broker.id)}
                        </Select.Option>
                      ))}
                    </Select>
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: 500 }}>
                      Strategy Name
                    </label>
                    <Input
                      placeholder="Search strategies..."
                      value={filters.strategyName}
                      onChange={(e) => setFilters(prev => ({ ...prev, strategyName: e.target.value }))}
                      allowClear
                      size="small"
                      style={{ height: "32px", width: "130px" }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: 500 }}>
                      Profit/Loss
                    </label>
                    <Select
                      style={{ width: '130px' }}
                      placeholder="All"
                      value={filters.profitLoss}
                      onChange={(val) => setFilters(prev => ({ ...prev, profitLoss: val }))}
                      allowClear
                    >
                      <Select.Option value="profit">Profit Making</Select.Option>
                      <Select.Option value="loss">Loss Making</Select.Option>
                    </Select>
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: 500 }}>
                      Status
                    </label>
                    <Select
                      style={{ width: '110px' }}
                      placeholder="All Status"
                      value={filters.status}
                      onChange={(val) => setFilters(prev => ({ ...prev, status: val === undefined ? null : val }))}
                      allowClear
                    >
                      <Select.Option value={0}>Stopped</Select.Option>
                      <Select.Option value={1}>Monitoring</Select.Option>
                      <Select.Option value={2}>Active</Select.Option>
                    </Select>
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: 500 }}>
                      Type
                    </label>
                    <Select
                      style={{ width: '100px' }}
                      placeholder="All Types"
                      value={filters.intraPos}
                      onChange={(val) => setFilters(prev => ({ ...prev, intraPos: val }))}
                      allowClear
                    >
                      <Select.Option value="intraday">Intraday</Select.Option>
                      <Select.Option value="positional">Positional</Select.Option>
                    </Select>
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: 500 }}>
                      Linked Signal
                    </label>
                    <Select
                      style={{ width: '130px' }}
                      placeholder="All Signals"
                      value={filters.linkedSignal}
                      onChange={(val) => setFilters(prev => ({ ...prev, linkedSignal: val }))}
                      allowClear
                      showSearch
                      optionFilterProp="children"
                    >
                      {getUniqueSignals().map(signal => (
                        <Select.Option key={signal} value={signal}>
                          {signal}
                        </Select.Option>
                      ))}
                    </Select>
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '12px', fontWeight: 500 }}>
                      Active Strategies
                    </label>
                    <Switch
                      checked={filters.activeStrategies}
                      onChange={(checked) => setFilters(prev => ({ ...prev, activeStrategies: checked }))}
                      size="small"
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'end', alignSelf: 'flex-end' }}>
                    <Button
                      type="default"
                      onClick={clearFilters}
                    >
                      Clear All
                    </Button>
                    <Button
                      type="default"
                      icon={<RefreshCw size={16} />}
                      onClick={handleRefresh}
                      loading={refreshing}
                    >
                      Refresh
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            <Table
              rowKey={(r) => r.brokerName}
              loading={loading}
              dataSource={rows}
              columns={columns}
              expandable={{
                expandedRowKeys: expandedBrokerKeys,
                onExpandedRowsChange: setExpandedBrokerKeys,
                expandedRowRender: (brokerRow) => {
                  const strategies = brokerToStrategies[brokerRow.brokerName] || [];
                  const filteredStrategies = applyFilters(brokerRow, strategies);
                  const currentBrokerId = brokerRow.brokerName;
                  const expandedRowRenderStrategy = (strategyRow) => (
                    <StrategyDataTabs
                      strategyId={strategyRow?.basketId?.[currentBrokerId]}
                      user={user}
                      socketJWT={socketJWT}
                      source={tradeFeedSource}
                      isBasketMode={true}
                    />
                  );
                  return (
                    <div style={{ padding: 8 }}>
                      <Table
                        rowKey={(r) => r.id}
                        dataSource={filteredStrategies}
                        columns={getStrategyColumns(currentBrokerId)}
                        pagination={false}
                        size="small"
                        scroll={{ x: true }}
                        expandable={{ expandedRowRender: expandedRowRenderStrategy }}
                      />
                    </div>
                  );
                }
              }}
              pagination={false}
              scroll={{ x: true }}
              size="small"
            />
            {/* SLTP Modal */}
            {isSLTPModalOpen && selectedStrategy && (
              <SLTPModal
                show={isSLTPModalOpen}
                setShow={setIsSLTPModalOpen}
                popUpData={{
                  row: {
                    basketId: selectedStrategy.basketId[selectedStrategy.currentBrokerId || selectedStrategy.brokers[0]],
                    data: []
                  },
                  headersValue: {
                    basketId: selectedStrategy.basketId[selectedStrategy.currentBrokerId || selectedStrategy.brokers[0]],
                    basketName: selectedStrategy.name,
                    totalPNL: selectedStrategy.totalPnl || 0,
                    combPrem: selectedStrategy.combPrem || 0
                  },
                  formData: {
                    ...(formDataMap?.[selectedStrategy?.basketId?.[selectedStrategy?.currentBrokerId || selectedStrategy?.brokers?.[0]]] || defaultRiskForm)
                  }
                }}
                istype="basket"
              />
            )}


            <SubscriptionCheckModal
              open={showSubscriptionModal}
              onClose={() => setShowSubscriptionModal(false)}
              type="Algo"
            />

            {/* PNL / MTM Modal */}
            {isPnlModalOpen && (
              <PnlMtmModal
                show={isPnlModalOpen}
                onHide={() => setIsPnlModalOpen(false)}
                title={pnlModalConfig.title}
                defaultLevel={pnlModalConfig.defaultLevel}
                brokerId={pnlModalConfig.brokerId}
                brokerName={pnlModalConfig.brokerName}
                baskets={pnlModalConfig.baskets}
                positions={pnlModalConfig.positions}
                selection={false}
              />
            )}
            <Modal
              title={rmsModalBroker ? `RMS Settings - ${rmsModalBroker.brokerName || ''}` : "RMS Settings"}
              visible={isRmsModalOpen}
              onCancel={() => { setIsRmsModalOpen(false); setRmsModalBroker(null); }}
              onOk={handleSaveRmsForBroker}
              okText="Save"
              cancelText="Cancel"
              width={450}
              okButtonProps={{ disabled: !!rmsErrorMessages.maxLoss || !!rmsErrorMessages.maxProfit }}
            >
              <>
                {rmsModalBroker && (
                  <div style={{ display: "flex" }} className="risk-modal-section risk-modal-section-locking">
                    <div className="risk-modal-input-container">
                      <label style={{ fontWeight: 500 }}>Max Loss</label>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <PlusMinQty
                          qtyClick={(val) => handleRmsChange("maxLoss", val, rmsModalBroker.brokerId)}
                          filterValue={getRmsForBroker(rmsModalBroker.brokerId).maxLoss}
                          name="maxLoss"
                          step={1}
                          max={0}
                          formatINR={true}
                        />
                        <Button
                          size="small"
                          icon={<X size={18} />}
                          onClick={() => handleRmsChange("maxLoss", null, rmsModalBroker.brokerId)}
                          danger
                          style={{ height: '24px' }}
                        />
                      </div>
                      {rmsErrorMessages.maxLoss && (
                        <div className="text-danger mt-1" style={{ fontSize: '10px', color: '#ff4d4f' }}>
                          {rmsErrorMessages.maxLoss}
                        </div>
                      )}
                    </div>
                    <div className="risk-modal-input-container">
                      <label style={{ fontWeight: 500 }}>Max Profit</label>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <PlusMinQty
                          qtyClick={(val) => handleRmsChange("maxProfit", val, rmsModalBroker.brokerId)}
                          filterValue={getRmsForBroker(rmsModalBroker.brokerId).maxProfit}
                          name="maxProfit"
                          min={0}
                          step={1}
                          formatINR={true}
                        />
                        <Button
                          size="small"
                          icon={<X size={18} />}
                          onClick={() => handleRmsChange("maxProfit", null, rmsModalBroker.brokerId)}
                          danger
                          style={{ height: '24px', marginLeft: '0.5px' }}
                        />
                      </div>
                      {rmsErrorMessages.maxProfit && (
                        <div className="text-danger mt-1" style={{ fontSize: '10px', color: '#ff4d4f' }}>
                          {rmsErrorMessages.maxProfit}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                <div className="info-note">
                  <ul>
                    <li>Once the set RMS Max Loss or Max Profit hit at the broker account level, all the open positions are closed and no new positions can be opened for the rest of the day. The limit resets automatically the next day.</li>
                  </ul>
                </div>
              </>
            </Modal>

            <BrokerLogsModal
              visible={logsModalVisible}
              onClose={() => {
                setLogsModalVisible(false);
                setSelectedBrokerForLogs({ id: null, name: "" });
              }}
              brokerId={selectedBrokerForLogs.id}
              brokerName={selectedBrokerForLogs.name}
            />

            <LinkedSignalsModal
              open={isLinkedSignalsModalOpen}
              onCancel={() => setIsLinkedSignalsModalOpen(false)}
              signalLinks={modalSignalLinks}
              strategyName={modalStrategyName}
            />
          </div>

        </div>
      </div>
    </div>
  );
};

export default BrokersPage;


